import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Plus, FileText, LogOut, Users } from 'lucide-react';
import { SecondaryButton } from './buttons';
import { loadDraft, clearDraft } from '../utils/draftStorage';
import { toast } from 'react-hot-toast';

interface HomePageProps {
    onLogout: () => void;
}

export const HomePage: React.FC<HomePageProps> = ({ onLogout }) => {
    const navigate = useNavigate();
    const [hasDraft, setHasDraft] = useState(false);
    const [logoError, setLogoError] = useState(false);

    const checkForDraft = async () => {
        try {
            const draft = await loadDraft();
            setHasDraft(!!draft);
        } catch (error) {
            console.error('Error checking for draft:', error);
        }
    };

    // Check for existing draft on mount
    useEffect(() => {
        checkForDraft();
    }, []);

    const startNewWizard = async () => {
        try {
            await clearDraft();
            navigate('/wizard');
            toast.success('Nova priorização iniciada');
        } catch (error) {
            console.error('Error clearing draft:', error);
            toast.error('Erro ao iniciar nova priorização');
        }
    };

    const continueDraft = () => {
        navigate('/wizard');
        toast.success('Rascunho carregado');
    };

    const handleLossesDelays = () => {
        navigate('/losses-delays');
    };

    const handleVendorView = () => {
        navigate('/dashboard');
    };

    return (
        <div className="min-h-screen bg-white">
            <div className="px-1 sm:px-6 py-8 sm:py-12">
                {/* Logout Button */}
                <div className="flex justify-end mb-6">
                    <SecondaryButton
                        onClick={onLogout}
                        leftIcon={<LogOut className="w-4 h-4" />}
                    >
                        Sair
                    </SecondaryButton>
                </div>

                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-12"
                >
                    {/* Dexco Logo */}
                    <div className="flex justify-center mb-6">
                        {!logoError ? (
                            <img
                                src="/wordmark.svg"
                                alt="Dexco"
                                className="h-16 w-auto"
                                onError={() => setLogoError(true)}
                            />
                        ) : (
                            <div className="text-4xl font-display font-bold text-dexco-green-primary">
                                DEXCO
                            </div>
                        )}
                    </div>

                    {/* Title and Description */}
                    <h1 className="text-3xl sm:text-4xl font-display font-bold text-dexco-text-dark mb-4">
                        Assistente de Priorização
                    </h1>
                    <p className="text-lg text-gray-600 font-body max-w-2xl mx-auto">
                        Sistema de planejamento e priorização de pedidos
                    </p>
                </motion.div>

                {/* Action Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl mx-auto mb-16">
                    {/* New Schedule Card */}
                    <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="bg-dexco-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 cursor-pointer hover:shadow-md transition-all duration-200"
                        onClick={startNewWizard}
                    >
                        <div className="flex flex-col items-center text-center">
                            <div className="flex items-center justify-center w-12 h-12 bg-dexco-green-primary rounded-lg mb-4">
                                <Plus className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="text-xl font-display font-semibold text-dexco-text-dark mb-2">
                                Nova priorização
                            </h3>
                            <p className="text-gray-600 font-body mb-4">
                                Inicie uma nova estratégia de priorização configurando cotas. limites e upload de produção.
                            </p>
                            <div className="flex items-center text-dexco-green-primary font-body font-medium">
                                Começar agora
                                <ChevronRight className="w-4 h-4 ml-1" />
                            </div>
                        </div>
                    </motion.div>

                    {/* Continue Draft Card */}
                    <motion.div
                        whileHover={{ scale: hasDraft ? 1.02 : 1 }}
                        whileTap={{ scale: hasDraft ? 0.98 : 1 }}
                        className={`bg-dexco-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 transition-all duration-200 ${hasDraft
                            ? 'cursor-pointer hover:shadow-md'
                            : 'opacity-60 cursor-not-allowed'
                            }`}
                        onClick={hasDraft ? continueDraft : undefined}
                    >
                        <div className="flex flex-col items-center text-center">
                            <div className={`flex items-center justify-center w-12 h-12 rounded-lg mb-4 ${hasDraft
                                ? 'bg-dexco-green-secondary'
                                : 'bg-gray-300'
                                }`}>
                                <FileText className={`w-6 h-6 ${hasDraft ? 'text-dexco-green-primary' : 'text-gray-500'}`} />
                            </div>
                            <h3 className="text-xl font-display font-semibold text-dexco-text-dark mb-2">
                                Continuar Rascunho
                            </h3>
                            <p className="text-gray-600 font-body mb-4">
                                {hasDraft
                                    ? 'Continue de onde parou com seu rascunho salvo automaticamente.'
                                    : 'Nenhum rascunho encontrado. Inicie um nova estrategia de priorização.'
                                }
                            </p>
                            {hasDraft && (
                                <div className="flex items-center text-dexco-green-primary font-body font-medium">
                                    Continuar
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>

                {/* Diretor Only Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl mx-auto mb-16">
                    {/* Informar Perdas e Atrasos Card */}
                    <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="bg-dexco-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 cursor-pointer hover:shadow-md transition-all duration-200"
                        onClick={handleLossesDelays}
                    >
                        <div className="flex flex-col items-center text-center">
                            <div className="flex items-center justify-center w-12 h-12 bg-dexco-green-primary rounded-lg mb-4">
                                <FileText className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="text-xl font-display font-semibold text-dexco-text-dark mb-2">
                                Informar Perdas e Atrasos
                            </h3>
                            <p className="text-sm text-gray-600 font-body">
                                Registre perdas de produção e atrasos nas fábricas para manter o sistema atualizado.
                            </p>
                        </div>
                    </motion.div>

                    {/* Visualização dos Vendedores Card */}
                    <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="bg-dexco-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 cursor-pointer hover:shadow-md transition-all duration-200"
                        onClick={handleVendorView}
                    >
                        <div className="flex flex-col items-center text-center">
                            <div className="flex items-center justify-center w-12 h-12 bg-blue-500 rounded-lg mb-4">
                                <Users className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="text-xl font-display font-semibold text-dexco-text-dark mb-2">
                                Visualização dos Vendedores
                            </h3>
                            <p className="text-sm text-gray-600 font-body">
                                Acesse a interface dos vendedores para visualizar gráficos e otimizações criadas.
                            </p>
                        </div>
                    </motion.div>
                </div>

                {/* Features Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-dexco-white rounded-xl shadow-lg p-4 sm:p-6 lg:p-8 border border-gray-200 max-w-4xl mx-auto"
                >
                    <h3 className="font-display font-semibold text-xl text-dexco-text-dark mb-6 text-center">
                        Como Funciona
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="text-center">
                            <div className="w-12 h-12 bg-dexco-green-primary rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                                <span className="text-white font-bold text-lg">1</span>
                            </div>
                            <h4 className="font-body font-semibold text-dexco-text-dark mb-2">
                                Produção e Cotas
                            </h4>
                            <p className="text-sm text-gray-600 font-body">
                                Upload de produção planejada e configure fatias percentuais dos vendedores.
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                                <span className="text-white font-bold text-lg">2</span>
                            </div>
                            <h4 className="font-body font-semibold text-dexco-text-dark mb-2">
                                Limites por Cliente
                            </h4>
                            <p className="text-sm text-gray-600 font-body">
                                Configure limites de volume por cliente para otimização da distribuição.
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                                <span className="text-white font-bold text-lg">3</span>
                            </div>
                            <h4 className="font-body font-semibold text-dexco-text-dark mb-2">
                                Gere Priorização
                            </h4>
                            <p className="text-sm text-gray-600 font-body">
                                Revise as configurações e gere a priorização otimizada.
                            </p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};